import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

const AddPractitionerForm = ({ onSubmit, onCancel, initialData = null, isEditing = false }) => {
  const [formData, setFormData] = useState({
    fullName: initialData?.name || '',
    title: initialData?.title || 'Dr.',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    specialization: initialData?.specialization || '',
    locationIds: initialData?.locationIds || [],
    bio: initialData?.bio || '',
    qualifications: initialData?.qualifications || '',
    experience: initialData?.experience || '',
    sendWelcomeEmail: !isEditing
  });

  const [availableLocations, setAvailableLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await apiService.getOffices();
        setAvailableLocations(response || []);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setAvailableLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const currentIds = prev.locationIds || [];
      const updated = currentIds.includes(locationId)
        ? currentIds.filter(id => id !== locationId)
        : [...currentIds, locationId];
      return { ...prev, locationIds: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleOptions = [
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Prof.', label: 'Prof.' }
  ];

  const specializationOptions = [
    { value: 'Physiotherapy', label: 'Physiotherapy' },
    { value: 'Osteopathy', label: 'Osteopathy' },
    { value: 'Sports Therapy', label: 'Sports Therapy' },
    { value: 'Massage Therapy', label: 'Massage Therapy' },
    { value: 'Acupuncture', label: 'Acupuncture' },
    { value: 'Chiropractic', label: 'Chiropractic' }
  ];

  return (
    <form onSubmit={handleSubmit} className="practitioner-form">
      <div className="form-content">
        {/* Basic Information */}
        <div className="form-section">
          <h4 className="section-title">Basic Information</h4>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input
                type="text"
                name="fullName"
                className="form-input"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Dr. John Smith"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Title</label>
              <select
                name="title"
                className="form-input"
                value={formData.title}
                onChange={handleChange}
              >
                {titleOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Email Address *</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="doctor@clinic.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                className="form-input"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+44 7123 456789"
                required
              />
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="form-section">
          <h4 className="section-title">Professional Information</h4>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Specialization</label>
              <input
                type="text"
                name="specialization"
                className="form-input"
                value={formData.specialization}
                onChange={handleChange}
                placeholder="e.g. Physiotherapy, Osteopathy (optional)"
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label className="form-label">
              Locations
              {formData.locationIds.length > 0 && (
                <span className="form-label-count"> ({formData.locationIds.length} selected)</span>
              )}
            </label>
            {locationsLoading ? (
              <div className="form-loading-state">Loading locations...</div>
            ) : availableLocations.length === 0 ? (
              <div className="form-empty-state">
                No locations found. Add locations in Practice Management first.
              </div>
            ) : (
              <div className="location-multi-select">
                {availableLocations.map(location => {
                  const isSelected = formData.locationIds.includes(location.id);
                  return (
                    <label
                      key={location.id}
                      className={`location-checkbox-item${isSelected ? ' selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleLocationToggle(location.id)}
                      />
                      <span className="location-checkbox-content">
                        <span className="location-checkbox-name">{location.name}</span>
                        {location.addressLine1 && (
                          <span className="location-checkbox-addr">{location.addressLine1}</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-group full-width">
            <label className="form-label">Years of Experience</label>
            <input
              type="text"
              name="experience"
              className="form-input"
              value={formData.experience}
              onChange={handleChange}
              placeholder="e.g., 5 years"
            />
          </div>
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h4 className="section-title">Additional Information</h4>

          <div className="form-group">
            <label className="form-label">Qualifications & Certifications</label>
            <textarea
              name="qualifications"
              className="form-input"
              value={formData.qualifications}
              onChange={handleChange}
              placeholder="List professional qualifications..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Professional Bio</label>
            <textarea
              name="bio"
              className="form-input"
              value={formData.bio}
              onChange={handleChange}
              placeholder="Brief professional biography..."
              rows={3}
            />
          </div>

          {!isEditing && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="sendWelcomeEmail"
                  checked={formData.sendWelcomeEmail}
                  onChange={handleChange}
                />
                <span>Send welcome email with login instructions</span>
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Practitioner' : 'Add Practitioner')}
        </button>
      </div>
    </form>
  );
};

export default AddPractitionerForm;
